import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { ClientOnly } from "vite-react-ssg";
import { Mail, CalendarDays } from "lucide-react";
import { cn } from "@/lib/utils";
import ContactFormBlock from "./ContactFormBlock";
import PrivacyNotice from "./PrivacyNotice";

// El embed real de Cal.com (mismo componente que las landings), en su propio
// chunk y sólo si hay enlace configurado.
const CalInlineEmbed = lazy(() => import("@/components/lp/CalInlineEmbed"));
const env = import.meta.env as Record<string, string | undefined>;

/**
 * Zona de contacto de la home con pestañas (en todos los tamaños): el usuario
 * elige "Agendar reunión" (calendario Cal.com) o "Escribir" (formulario). Ambos
 * paneles quedan montados y se alternan con CSS (el iframe del calendario no se
 * recarga al cambiar de pestaña). Conserva id="contact-info" (hero y menú).
 */
const ContactSection = () => {
  const calLink = env.VITE_CALCOM_LINK;
  const [tab, setTab] = useState<"agenda" | "mensaje">("agenda");
  // El embed de Cal.com (~1MB de iframe) sólo se monta cuando la sección se
  // acerca al viewport, para no competir con la carga inicial de la página.
  const sectionRef = useRef<HTMLElement>(null);
  const [loadCal, setLoadCal] = useState(false);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || loadCal) return;
    // Deep-link directo a la sección (CTA del hero, enlaces #contact…).
    if (window.location.hash.startsWith("#contact")) {
      setLoadCal(true);
      return;
    }
    if (!("IntersectionObserver" in window)) {
      setLoadCal(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setLoadCal(true);
          io.disconnect();
        }
      },
      { rootMargin: "1200px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loadCal]);

  return (
    <section ref={sectionRef} id="contact-info" className="bg-gradient-to-b from-white to-gray-50 py-16 md:py-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="inline-block mb-3 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium">
            ¿Hablamos?
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Agenda una reunión o escríbenos</h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Reserva un hueco para hablar 20 minutos, o mándanos un mensaje y te respondemos pronto. Tú eliges.
          </p>
        </div>

        {/* Pestañas */}
        <div className="flex gap-1 mb-8 p-1 bg-gray-100 rounded-xl max-w-md mx-auto" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "agenda"}
            onClick={() => setTab("agenda")}
            className={cn(
              "flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5",
              tab === "agenda" ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-800"
            )}
          >
            <CalendarDays className="w-4 h-4" />
            Agendar reunión
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "mensaje"}
            onClick={() => setTab("mensaje")}
            className={cn(
              "flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5",
              tab === "mensaje" ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-800"
            )}
          >
            <Mail className="w-4 h-4" />
            Escribir
          </button>
        </div>

        {/* Panel: Agendar */}
        <div className={tab === "agenda" ? "block" : "hidden"}>
          <div
            className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
            style={{ minHeight: 560 }}
          >
            {calLink ? (
              <ClientOnly fallback={<div className="p-12 text-center text-gray-500">Cargando calendario…</div>}>
                {() =>
                  loadCal ? (
                    <Suspense fallback={<div className="p-12 text-center text-gray-500">Cargando calendario…</div>}>
                      <CalInlineEmbed calLink={calLink} landing="home" />
                    </Suspense>
                  ) : (
                    <div className="p-12 text-center text-gray-500">Cargando calendario…</div>
                  )
                }
              </ClientOnly>
            ) : (
              <div className="p-12 text-center text-gray-600">El calendario estará disponible en breve.</div>
            )}
          </div>
          <div className="max-w-2xl mx-auto">
            <PrivacyNotice />
          </div>
        </div>

        {/* Panel: Escribir (formulario más estrecho que el calendario) */}
        <div className={tab === "mensaje" ? "block max-w-2xl mx-auto" : "hidden"}>
          <ContactFormBlock />
          <div className="text-center mt-6">
            <p className="text-gray-600 mb-2 text-sm">Escríbenos directamente a:</p>
            <a
              href="mailto:jordi.reina@kratia.ai"
              className="inline-flex items-center text-primary hover:text-primary/80 font-medium"
            >
              <Mail className="w-5 h-5 mr-2" />
              jordi.reina@kratia.ai
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
